/**
 * Client-side ranking over the docs search index (ported from the legacy
 * `/docs/search` scorer): weighted term matches on title/description/heading/
 * content, boosted by how many query terms hit, with a snippet excerpt.
 */
import type { SearchDocPage, SearchIndex } from "@/features/docs/types";

export interface SearchResult {
  page: SearchDocPage;
  score: number;
  snippet: string;
}

/**
 * A ±window around the first match, snapped to readable boundaries.
 *
 * The raw window opened mid-word and mid-sentence — a hit on `swipePoly` in a
 * method table rendered as "…erforms multiple taps in sequence with
 * configurable delays. swipePoly(startX…", which is the *previous* method's
 * description with a fragment of a word on the front. Prefer to start just
 * after a sentence break when one is close, otherwise at the next word
 * boundary, and always end on one.
 */
function snippetFor(content: string, term: string): string {
  const lower = content.toLowerCase();
  const idx = lower.indexOf(term);
  if (idx === -1) return content.slice(0, 140).trimEnd();

  let start = Math.max(0, idx - 70);
  if (start > 0) {
    const lead = content.slice(start, idx);
    const brk = Math.max(lead.lastIndexOf(". "), lead.lastIndexOf("; "));
    if (brk !== -1) {
      start += brk + 2;
    } else {
      const space = content.indexOf(" ", start);
      if (space !== -1 && space < idx) start = space + 1;
    }
  }

  let end = Math.min(content.length, idx + 110);
  if (end < content.length) {
    const space = content.lastIndexOf(" ", end);
    if (space > idx) end = space;
  }

  const body = content.slice(start, end).trim();
  return `${start > 0 ? "…" : ""}${body}${end < content.length ? "…" : ""}`;
}

export function searchDocs(index: SearchIndex, query: string): SearchResult[] {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= 2);
  if (terms.length === 0) return [];

  const results: SearchResult[] = [];
  for (const page of index.pages) {
    const title = page.title.toLowerCase();
    const description = (page.description ?? "").toLowerCase();
    const headings = page.headings.join(" ").toLowerCase();
    const content = page.content.toLowerCase();

    let score = 0;
    let matched = 0;
    for (const term of terms) {
      let hit = false;
      if (title.includes(term)) {
        score += 10;
        hit = true;
      }
      if (description.includes(term)) {
        score += 5;
        hit = true;
      }
      if (headings.includes(term)) {
        score += 3;
        hit = true;
      }
      if (content.includes(term)) {
        score += 1;
        hit = true;
      }
      if (hit) matched += 1;
    }
    if (score === 0) continue;
    score *= matched / terms.length;
    results.push({
      page,
      score,
      snippet: snippetFor(page.content || page.description || "", terms[0]!),
    });
  }

  return results.sort((a, b) => b.score - a.score).slice(0, 25);
}
